import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import PageTest from "../common/PageTest";
import AuthModal from "../modals/AuthModal";
import ReactDOM from "react-dom";
import { useAuthModal } from "../modals/AuthModalContext";
import { useAuth, useGoogleAuth } from "../modals/AuthContext";
import { getAvatarFallbackUrl } from "../../utils/helper";
import {
  hasPendingTestResults,
  PENDING_TEST_RESULTS_EVENT,
} from "../../utils/pendingTestResults";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const hamburgerMenuRef = useRef(null);
  const hamburgerButtonRef = useRef(null);
  const { authModalOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const { user, isAuthenticated, logout } = useAuth();
  const { googleProfile, profilePicture } = useGoogleAuth();
  const [hasTestReport, setHasTestReport] = useState(hasPendingTestResults);

  useEffect(() => {
    const sync = () => setHasTestReport(hasPendingTestResults());
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(PENDING_TEST_RESULTS_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(PENDING_TEST_RESULTS_EVENT, sync);
    };
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen((open) => {
      if (!open) setIsDropdownOpen(false);
      return !open;
    });
  };
  const toggleDropdown = () => {
    setIsDropdownOpen((open) => {
      if (!open) setIsMenuOpen(false);
      return !open;
    });
  };

  // Close the menu whenever the location changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Scroll to the top when the location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Close dropdowns on outside click (profile and hamburger)
  useEffect(() => {
    function handleClickOutside(event) {
      // Profile dropdown
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        dropdownMenuRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !dropdownMenuRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
      // Hamburger menu
      if (
        isMenuOpen &&
        hamburgerMenuRef.current &&
        !hamburgerMenuRef.current.contains(event.target) &&
        (!hamburgerButtonRef.current ||
          !hamburgerButtonRef.current.contains(event.target))
      ) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, isMenuOpen]);

  const navLinkClass = (path, mobile = false) => {
    const isActive = location.pathname === path;
    const base = mobile
      ? "block px-3 py-2 rounded-lg text-base font-medium transition-colors"
      : "px-3 py-2 rounded-lg text-sm font-medium transition-colors";
    return `${base} ${
      isActive
        ? "text-foreground bg-accent"
        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
    }`;
  };

  return (
    <nav
      id="navbar"
      className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 w-full">
          {/* Hamburger menu for mobile */}
          <div className="flex items-center sm:hidden mr-4">
            <button
              ref={hamburgerButtonRef}
              onClick={toggleMenu}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              aria-controls="mobile-menu"
              aria-expanded={isMenuOpen ? "true" : "false"}
              id="hamburger"
            >
              <span className="sr-only">
                {isMenuOpen ? "Close main menu" : "Open main menu"}
              </span>
              {isMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
          {/* Logo - left-aligned */}
          <div className="flex items-center flex-shrink-0">
            <PageTest />
          </div>
          {/* Spacer to push links to the right */}
          <div className="flex-1" />
          {/* Nav links + profile */}
          <div className="hidden sm:flex items-center gap-1">
            <Link to="/test" className={navLinkClass("/test")}>
              {hasTestReport ? "Test Report" : "Run Test"}
            </Link>
            {isAuthenticated && (
              <Link to="/dashboard" className={navLinkClass("/dashboard")}>
                Dashboard
              </Link>
            )}
          </div>
          <div className="flex items-center ml-2 sm:ml-3 relative">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={() => openAuthModal("auth")}
                  className="h-9 flex items-center px-3 border border-border text-foreground rounded-lg hover:bg-muted transition-all text-sm font-medium"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                <button
                  ref={dropdownRef}
                  onClick={toggleDropdown}
                  className="flex items-center justify-center w-9 h-9 rounded-full focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                  aria-label="Account menu"
                  style={{
                    background: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  <img
                    src={
                      profilePicture ||
                      getAvatarFallbackUrl(
                        user?.name ||
                          googleProfile?.name ||
                          user?.email ||
                          googleProfile?.email ||
                          "User",
                      )
                    }
                    alt={googleProfile?.name || user?.name || "User"}
                    className="w-9 h-9 rounded-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getAvatarFallbackUrl(
                        user?.name ||
                          googleProfile?.name ||
                          user?.email ||
                          googleProfile?.email ||
                          "User",
                      );
                    }}
                  />
                </button>
                {isDropdownOpen &&
                  ReactDOM.createPortal(
                    <div
                      ref={dropdownMenuRef}
                      className="fixed z-[50] w-72 bg-card rounded-lg shadow-subtle border border-border animate-fade-in-fast flex flex-col"
                      style={(() => {
                        const btn = dropdownRef.current;
                        if (!btn) return { right: 16, top: 64 };
                        const rect = btn.getBoundingClientRect();
                        return {
                          right: window.innerWidth - rect.right,
                          top: rect.bottom + 8,
                        };
                      })()}
                    >
                      <div className="flex items-center space-x-3 p-4 border-b">
                        <img
                          src={
                            profilePicture ||
                            getAvatarFallbackUrl(
                              user?.name ||
                                googleProfile?.name ||
                                user?.email ||
                                googleProfile?.email ||
                                "User",
                            )
                          }
                          alt={googleProfile?.name || user?.name || "User"}
                          className="w-12 h-12 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = getAvatarFallbackUrl(
                              user?.name ||
                                googleProfile?.name ||
                                user?.email ||
                                googleProfile?.email ||
                                "User",
                            );
                          }}
                        />
                        <div className="flex flex-col min-w-0">
                          <div className="font-semibold text-lg text-foreground truncate">
                            {user?.name || user?.email || "User"}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {user?.email?.toLowerCase()}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate("/profile");
                        }}
                        className="flex items-center px-4 py-3 text-foreground hover:bg-muted transition text-sm font-medium w-full text-left mt-2"
                      >
                        <UserCircleIcon className="w-5 h-5 mr-2" />
                        Profile
                      </button>
                      <div className="border-t my-2" />
                      <button
                        onClick={() => {
                          logout();
                          setIsDropdownOpen(false);
                        }}
                        className="flex items-center px-4 py-3 text-red-600 hover:bg-red-50 transition text-sm font-medium w-full text-left mb-2"
                      >
                        <ArrowLeftOnRectangleIcon className="w-5 h-5 mr-2" />
                        Log out
                      </button>
                    </div>,
                    document.body,
                  )}
              </>
            )}
          </div>
          {/* Always render AuthModal so it can be triggered globally */}
          <AuthModal open={authModalOpen} onClose={closeAuthModal} />
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`${isMenuOpen ? "block" : "hidden"} sm:hidden`}
        id="mobile-menu"
        ref={hamburgerMenuRef}
      >
        <div className="space-y-1 px-4 pt-2 pb-3 border-t border-border bg-background">
          <Link to="/test" className={navLinkClass("/test", true)}>
            {hasTestReport ? "Test Report" : "Run Test"}
          </Link>
          {isAuthenticated && (
            <Link to="/dashboard" className={navLinkClass("/dashboard", true)}>
              Dashboard
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
